import * as React from 'react';
import { useState } from 'react';
import area from '@turf/area';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import CircularProgress from '@material-ui/core/CircularProgress';
import { uploadGeoJsonAsPlace } from '../../utils/geojsonUpload';

interface ControlPanelProps {
  containerComponent: any;
  polygon: any;
  lngLat: [number, number] | null; // Change to array format
  userData?: any; // Add userData prop
  onPolygonSaved?: () => void; // Callback when polygon is saved
  onNavigateToLocation?: (organization: string, slug: string) => void; // Navigate to location
}

// Memoize the area calculation to avoid recalculating on every render
const calculatePolygonArea = (polygon) => {
  if (!polygon) return null;

  try {
    let geoJsonInput = polygon;

    if (polygon.type === 'FeatureCollection') {
      geoJsonInput = polygon;
    } else if (polygon.type === 'Feature') {
      geoJsonInput = {
        type: 'FeatureCollection',
        features: [polygon],
      };
    } else if (polygon.type === 'Polygon' || polygon.type === 'MultiPolygon') {
      geoJsonInput = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: polygon,
          },
        ],
      };
    }

    if (geoJsonInput.type === 'FeatureCollection' && geoJsonInput.features) {
      const validFeatures = geoJsonInput.features.filter((feature) => {
        return (
          feature.geometry &&
          (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') &&
          feature.geometry.coordinates &&
          feature.geometry.coordinates.length > 0
        );
      });

      if (validFeatures.length === 0) {
        return null;
      }

      const cleanGeoJson = {
        type: 'FeatureCollection' as const,
        features: validFeatures,
      };

      return area(cleanGeoJson) / 1e6; // Convert to km²
    }

    return null;
  } catch (error) {
    console.error('Error calculating polygon area:', error);
    return null;
  }
};

const ControlPanel: React.FC<ControlPanelProps> = ({
  polygon,
  lngLat,
  userData,
  onPolygonSaved,
  onNavigateToLocation,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aoiName, setAoiName] = useState('');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const handleSaveClick = () => {
    setDialogOpen(true);
    setAoiName('');
  };

  const handleDialogClose = () => {
    if (!loading) {
      setDialogOpen(false);
      setAoiName('');
    }
  };

  const handleUpload = async () => {
    if (!aoiName.trim()) {
      setNotification({
        open: true,
        message: 'Please enter a name for the AOI',
        severity: 'error',
      });
      return;
    }

    if (!userData) {
      setNotification({
        open: true,
        message: 'User data not available. Please try refreshing the page.',
        severity: 'error',
      });
      return;
    }

    setLoading(true);
    try {
      const result = await uploadGeoJsonAsPlace(polygon, aoiName, userData);

      setNotification({
        open: true,
        message: `AOI "${aoiName}" saved successfully!`,
        severity: 'success',
      });
      setDialogOpen(false);
      setAoiName('');

      // Clear the polygon from the map after successful save
      if (onPolygonSaved) {
        onPolygonSaved();
      }

      // Navigate to the new AOI - try multiple possible locations for slug
      const slug = result?.data?.slug || result?.slug;
      const organization = userData?.organization;

      if (onNavigateToLocation && slug && organization) {
        onNavigateToLocation(organization, slug);

        // Dispatch custom event to notify the chat about the new AOI
        const aoiCreatedEvent = new CustomEvent('aoi-created', {
          detail: {
            aoiName,
            aoiSlug: slug,
            organization,
          },
        });
        window.dispatchEvent(aoiCreatedEvent);
      }
    } catch (error) {
      const message = (error as any).message || 'Failed to save AOI';
      setNotification({
        open: true,
        message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleUpload();
    }
  };
  const defaultLngLat: [number, number] = [0, 0];

  // Handle array format directly
  const safeLngLat = lngLat || defaultLngLat;
  const [lng, lat] = safeLngLat;

  const polygonArea = calculatePolygonArea(polygon);

  // Format coordinates with proper cardinal directions
  const latDirection = lat >= 0 ? 'N' : 'S';
  const lngDirection = lng >= 0 ? 'E' : 'W';
  const formattedLat = Math.abs(lat).toFixed(4);
  const formattedLng = Math.abs(lng).toFixed(4);

  // Dynamic height based on polygon presence
  const baseHeight = 32;
  const expandedHeight = baseHeight * 3; // 3x the default height
  const currentHeight = polygon ? expandedHeight : baseHeight;

  return (
    <>
      <div
        className="control-panel"
        style={{
          width: '200px',
          height: `${currentHeight}px`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          textAlign: 'center',
          backgroundColor: '#455A64',
          color: '#FFFFFF',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: '4px',
          padding: '8px 12px',
          fontSize: '16px',
          fontFamily: 'Roboto',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          pointerEvents: 'none',
          transition: 'height 0.3s ease',
        }}
      >
        <div style={{ position: 'relative' }}>
          <p
            onClick={() => {
              if (lngLat) {
                const coordText = `${formattedLat}°${latDirection}, ${formattedLng}°${lngDirection}`;
                try {
                  navigator.clipboard.writeText(coordText);
                } catch (error) {
                  console.error('Failed to copy coordinates:', error);
                }
              }
            }}
            style={{
              margin: '2px 0',
              color: '#FFFFFF',
              fontSize: '16px',
              cursor: lngLat ? 'pointer' : 'default',
              pointerEvents: 'auto',
              userSelect: 'text',
              transition: 'opacity 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (lngLat) {
                e.currentTarget.style.opacity = '0.8';
                setShowTooltip(true);
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              setShowTooltip(false);
            }}
          >
            {lngLat
              ? `${formattedLat}°${latDirection}, ${formattedLng}°${lngDirection}`
              : 'Move mouse over map'}
          </p>
          {showTooltip && lngLat && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginTop: '8px',
                backgroundColor: '#455A64',
                color: '#FFFFFF',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: 'Roboto',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                zIndex: 1000,
              }}
            >
              Click to copy coordinates
            </div>
          )}
        </div>
        {polygon && (
          <div>
            <p
              style={{
                margin: '2px 0 8px 0',
                color: '#FFFFFF',
                fontSize: '16px',
                letterSpacing: '0.5px',
              }}
            >
              Area:{' '}
              {polygonArea
                ? polygonArea > 1000
                  ? Math.round(polygonArea)
                  : polygonArea.toFixed(2)
                : 'N/A'}{' '}
              km²
            </p>
            <button
              onClick={handleSaveClick}
              style={{
                backgroundColor: 'transparent',
                color: '#FFFFFF',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '2px',
                padding: '2px 8px',
                fontSize: '12px',
                cursor: 'pointer',
                fontFamily: 'Roboto',
                fontWeight: '400',
                transition: 'all 0.2s ease',
                pointerEvents: 'auto',
                marginTop: '4px',
                marginBottom: '6px',
                outline: 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)';
              }}
            >
              Save as AOI
            </button>
          </div>
        )}
      </div>

      {/* Dialog for AOI name input */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Save Polygon as AOI</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="AOI Name"
            type="text"
            fullWidth
            value={aoiName}
            onChange={(e) => setAoiName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter AOI name"
            disabled={loading}
            helperText="Enter a unique name for this Area of Interest"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} color="primary" disabled={loading}>
            {loading ? (
              <>
                <CircularProgress size={20} style={{ marginRight: 8 }} />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success/error notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        message={notification.message}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        style={{ marginTop: '80px' }}
      />
    </>
  );
};

export default ControlPanel;
